/*
 * *
 *   ~  * Copyright (c) by INFODATA-GROUP. All rights reserved.
 *   ~  * No part of this publication may be reproduced, distributed,
 *   ~  * or transmitted in any form or by any means, including photocopying, recording,
 *   ~  * or other electronic or mechanical methods, without the prior written permission of the publisher,
 *   ~  * except in the case of brief quotations embodied in critical
 *   ~  * reviews and certain other noncommercial uses permitted by copyright law.
 *   ~  * AUTHOR : bs
 *   ~  * DATE : 06/05/2025 11:54
 *
 */

import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import {
  ActionEvent,
  DataModel,
  Factory,
  RestService,
  StandardAction,
  StandardActionBuilder,
  StandardEntity,
} from '@infodata-group/inf-commons';
import {
  ActionsBarBuilder,
  ActionsBarComponent,
  FormComponent,
} from '@infodata-group/inf-commons-desktop';

@Component({
  selector: 'infl-standard-page-form',
  standalone: true,
  imports: [ActionsBarComponent, FormComponent],
  templateUrl: './standard-page-form.component.html',
  styleUrl: './standard-page-form.component.scss',
})
export class StandardPageFormComponent<T extends StandardEntity>
  implements OnChanges
{
  private restService = inject(RestService);

  @Input({ required: true }) model: DataModel;
  @Input({ required: true }) factory: Factory<T>;
  @Input({ required: true }) entity: T;
  protected formType: string;

  @Output() actionClick = new EventEmitter<ActionEvent>();

  public title = '';
  public actionBarConfig = new ActionsBarBuilder().setTitleStyle().build();
  public actionsGroup: StandardAction[] = [
    new StandardActionBuilder('save', 'save')
      .setTooltip('i18n.commons-lib.standard-page.save')
      .build(),
    new StandardActionBuilder('close', 'xmark')
      .setTooltip('i18n.commons-lib.standard-page.close')
      .build(),
  ];

  @ViewChild('form') form: FormComponent;

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.hasOwn(changes, 'entity')) {
      if (this.entity) {
        this.title = this.entity.getLabel()
          ? this.entity.getLabel()
          : 'i18n.commons-lib.standard-page.edit';
        this.formType = 'edit';
      } else {
        this.title = 'i18n.commons-lib.standard-page.new';
        this.formType = 'create';
      }
    }
  }

  onClickAction(event: ActionEvent) {
    switch (event.action.name) {
      case 'save':
        this.save();
        break;
      case 'close':
        this.actionClick.emit(
          new ActionEvent('click', new StandardAction('close', '')),
        );
        break;
      default:
        console.log(`Action : ${event.action.name} is unable`);
    }
  }

  public save(): void {
    const data = this.form.validate();

    if (!data) {
      return;
    }

    if (this.formType === 'create') {
      this.post(data);
      return;
    }
    data.id = this.entity.getId();
    this.put(data);
  }

  public post(data: T): void {
    this.restService.post(this.factory.getApi(), data).subscribe({
      next: () => {
        this.actionClick.emit(
          new ActionEvent('click', new StandardAction('post', ''), data.id),
        );
      },
    });
  }

  public put(data: T): void {
    const id = this.entity.getId();
    this.restService.put(`${this.factory.getApi()}${id}`, data).subscribe({
      next: () => {
        this.actionClick.emit(
          new ActionEvent('click', new StandardAction('put', ''), data.id),
        );
      },
    });
  }
}
